import { z } from 'zod';
import { useQueries, useQuery } from '@tanstack/react-query';
import { apiClient } from './client';
import {
  EngagementDetailDtoSchema,
  type EngagementDetailDto,
  type EngagementDto,
} from './engagements';

/**
 * Portfolio dashboard API surface (T-0107 / C20-117 / E10 wave 6).
 *
 * The portfolio landing route at `/` lists the engagements a user can
 * see as a grid of cards plus a cross-engagement activity feed sidebar.
 *
 * Wave 9 backend ship (T-0081 / PR #128) — single composite read
 * --------------------------------------------------------------------
 * Pre-Wave-9 this route fanned out `1 + 3N` requests per render
 * (engagement list + posture + scorecard per engagement). The T-0081
 * composite endpoint:
 *
 *   GET /api/v1/portfolio?top_risk_limit=N
 *     → { summary, engagements[], top_risks[], computed_at }
 *
 * collapses the engagement list + per-engagement posture + per-
 * engagement finding/overdue counts into ONE round-trip. This file now
 * exposes the composite via `usePortfolioQuery()`, plus shims for the
 * historic fan-out call sites (C20-130 retired the engagement-card grid in
 * favour of the prototype's table layout; the shims are retained so any
 * remaining callers — and the activity-feed audit fan-out — keep working
 * without churn):
 *
 *   - `usePortfolioEngagementsQuery()` derives the engagement list from
 *     the composite payload (back-compat with the existing index.tsx).
 *   - `useEngagementPosturesQuery()` reads the per-engagement posture
 *     from the composite + serves the existing `OverallPosture`-shaped
 *     result (composite carries the colour; the per-domain detail
 *     stays a separate per-engagement endpoint).
 *
 * Surfaces still NOT in the composite (separate fan-outs preserved):
 *   - Per-engagement readiness scorecard (Go-Live decision pill on each
 *     card). Composite ships the posture rollup; the scorecard rec lives
 *     in `services/readiness-scorecard`.
 *   - Per-engagement readiness trend (sparkline). Composite ships
 *     last-activity timestamp; the trend points come from T-0088's
 *     `/readiness/trend` endpoint.
 *   - Per-engagement audit page (activity feed). No unified cross-
 *     engagement audit endpoint exists today (flagged in T-0107 §6
 *     risks). v1 keeps the per-engagement fan-out.
 *
 * Posture vocabulary widening
 * ---------------------------
 * The composite introduces an `unassessed` variant in the engagement
 * posture enum (separate from `grey` — engagements with no committed
 * assessments at all). The schema below uses
 * `PortfolioPostureSchema = green | yellow_divergent | red | grey |
 * unassessed`. `rollupColorToPostureStatus` (in `health-posture.ts`)
 * already maps both `grey` and the absence of a colour to the
 * `unassessed` posture-chip variant, so chip rendering stays consistent.
 */

// ===========================================================================
// Composite shape — what the backend returns.
// ===========================================================================

export const PortfolioPostureSchema = z.enum([
  'green',
  'yellow_divergent',
  'red',
  'grey',
  'unassessed',
]);
export type PortfolioPosture = z.infer<typeof PortfolioPostureSchema>;

const FindingCountsSchema = z.object({
  critical: z.number(),
  high: z.number(),
  medium: z.number(),
  low: z.number(),
  info: z.number(),
});
export type FindingCountsBySeverity = z.infer<typeof FindingCountsSchema>;

export const PortfolioEngagementDtoSchema = z
  .object({
    id: z.string().uuid(),
    project_id: z.string().uuid(),
    client_id: z.string().uuid(),
    client_name: z.string(),
    name: z.string(),
    status: z.string(),
    modules_enabled: z.array(z.string()),
    banner_image_url: z.string().nullable(),
    posture: PortfolioPostureSchema,
    open_finding_count: z.number(),
    finding_counts_by_severity: FindingCountsSchema,
    overdue_deliverable_count: z.number(),
    last_activity_at: z.string().nullable(),
    created_at: z.string(),
  })
  .passthrough();
export type PortfolioEngagementDto = z.infer<typeof PortfolioEngagementDtoSchema>;

const PortfolioTopRiskEntrySchema = z.object({
  engagement_id: z.string().uuid(),
  risk_score: z.number(),
  contributing_signals: z.object({
    posture: PortfolioPostureSchema,
    posture_distance: z.number(),
    finding_severity_total: z.number(),
    overdue_count: z.number(),
    staleness_days: z.number(),
    days_since_last_activity: z.number().nullable(),
  }),
});
export type PortfolioTopRiskEntry = z.infer<typeof PortfolioTopRiskEntrySchema>;

const PortfolioSummarySchema = z.object({
  engagement_count: z.number(),
  posture_counts: z.object({
    green: z.number(),
    yellow_divergent: z.number(),
    red: z.number(),
    grey: z.number(),
    unassessed: z.number(),
  }),
  open_finding_total: z.number(),
  overdue_deliverable_total: z.number(),
});
export type PortfolioSummary = z.infer<typeof PortfolioSummarySchema>;

export const PortfolioResultSchema = z.object({
  summary: PortfolioSummarySchema,
  engagements: z.array(PortfolioEngagementDtoSchema),
  top_risks: z.array(PortfolioTopRiskEntrySchema),
  computed_at: z.string(),
});
export type PortfolioResult = z.infer<typeof PortfolioResultSchema>;

export interface GetPortfolioOptions {
  /** Top-risk slice size. Default 10, max 50. */
  top_risk_limit?: number;
}

export function getPortfolio(opts: GetPortfolioOptions = {}): Promise<PortfolioResult> {
  const query: Record<string, string | number | undefined> = {};
  if (opts.top_risk_limit !== undefined) query.top_risk_limit = opts.top_risk_limit;
  return apiClient('/api/v1/portfolio', PortfolioResultSchema, { query });
}

/**
 * The portfolio dashboard's primary read. One round-trip; returns the
 * full posture + finding + overdue rollup for every accessible
 * engagement plus the top-risk ordered slice.
 */
export function usePortfolioQuery(opts: GetPortfolioOptions = {}) {
  return useQuery({
    queryKey: ['portfolio', 'composite', opts] as const,
    queryFn: () => getPortfolio(opts),
    staleTime: 60_000,
  });
}

// ===========================================================================
// Back-compat fan-out shims — the index.tsx route binds against the
// historic per-engagement hooks; these now derive from the composite
// query rather than firing N requests.
// ===========================================================================

import {
  type OverallPosture,
  rollupColorToPostureStatus as rollupToPostureStatusInternal,
} from './health-posture';

/** Re-export so callers that imported it from this module keep working. */
export { rollupToPostureStatusInternal as rollupColorToPostureStatus };

/**
 * The historic EngagementListResponse shape — derived from the composite
 * portfolio read. `next_cursor` is omitted because the composite returns
 * every accessible engagement in one shot (single-firm v1; ~200
 * engagements per spec §13).
 */
export interface EngagementListResponse {
  engagements: EngagementDto[];
  next_cursor?: string;
}

export interface ListEngagementsOpts {
  for_user?: 'me';
  client_id?: string;
  status?: 'setup' | 'active' | 'archived';
  cursor?: string;
  limit?: number;
}

/**
 * Project a `PortfolioEngagementDto` row into the historic `EngagementDto`
 * shape — preserves the fields the engagement-card binds against and
 * leaves the timestamps on `created_at` so consumers that read the dtos
 * for project metadata don't break.
 */
function projectToEngagementDto(row: PortfolioEngagementDto): EngagementDto {
  return {
    id: row.id,
    project_id: row.project_id,
    project_name: row.name,
    client_id: row.client_id,
    client_name: row.client_name,
    name: row.name,
    status: row.status,
    modules_enabled: row.modules_enabled,
    banner_image_url: row.banner_image_url,
    created_at: row.created_at,
  };
}

/**
 * Caller-scoped engagement list — derived from the composite portfolio
 * read so existing call sites only do one round-trip. The opts arg is
 * preserved for back-compat but `client_id` / `status` / `cursor` /
 * `limit` are ignored at this layer (the composite is unconditionally
 * the caller's full set). Filtering happens client-side in the route.
 */
export function usePortfolioEngagementsQuery(_opts: ListEngagementsOpts = {}) {
  const portfolioQuery = usePortfolioQuery();
  return {
    ...portfolioQuery,
    data: portfolioQuery.data
      ? {
          engagements: portfolioQuery.data.engagements.map(projectToEngagementDto),
        }
      : undefined,
  };
}

/**
 * Map composite posture → historic `OverallPosture` shape. The composite
 * surface only carries the engagement-level rollup colour; the per-
 * domain breakdown stays a separate per-engagement endpoint. The
 * synthetic `OverallPosture` here surfaces just the rollup so the
 * existing engagement-card can render the chip without rewiring.
 *
 * `RollupColor` was widened to include `unassessed` so both colour sets
 * round-trip through the chip vocabulary cleanly (see
 * `health-posture.ts` for the union).
 */
function projectToOverallPosture(row: PortfolioEngagementDto): OverallPosture {
  return {
    overall_rollup: row.posture,
    healthy_count: 0,
    total_active_domains: 0,
    is_fully_healthy: false,
    per_domain: [],
  };
}

/**
 * Posture hook — preserved signature, now backed by the composite read.
 * Returns a `useQueries`-shaped array so the existing index.tsx zip-by-
 * index access pattern (`postureQueries[idx]?.data`) stays intact.
 */
export function useEngagementPosturesQuery(engagementIds: ReadonlyArray<string>) {
  const portfolio = usePortfolioQuery();
  const byId = new Map<string, PortfolioEngagementDto>();
  for (const row of portfolio.data?.engagements ?? []) byId.set(row.id, row);
  return engagementIds.map((id) => {
    const row = byId.get(id);
    return {
      data: row ? projectToOverallPosture(row) : undefined,
      isLoading: portfolio.isLoading,
      isError: portfolio.isError,
      error: portfolio.error,
    };
  });
}

// ===========================================================================
// Per-card sparkline data — last-5 readiness snapshots per engagement.
// Composite does NOT ship trend points (T-0088 owns the time series);
// fan-out preserved here.
// ===========================================================================

import { getReadinessTrend, type ReadinessTrendResult } from './readiness-trend';

export function useEngagementTrendsQuery(engagementIds: ReadonlyArray<string>) {
  return useQueries({
    queries: engagementIds.map((id) => ({
      queryKey: ['portfolio', 'engagement-trend', id] as const,
      queryFn: () => getReadinessTrend(id, { limit: 5 }),
      staleTime: 60_000,
    })),
  });
}

// ===========================================================================
// Per-engagement scorecard — Go-Live recommendation pill on each card.
// Composite does NOT ship the scorecard (T-0089 owns readiness rubric);
// fan-out preserved here.
// ===========================================================================

import { getReadinessScorecard, type ReadinessScorecard } from './readiness';

export function useEngagementScorecardsQuery(engagementIds: ReadonlyArray<string>) {
  return useQueries({
    queries: engagementIds.map((id) => ({
      queryKey: ['portfolio', 'engagement-scorecard', id] as const,
      queryFn: () => getReadinessScorecard(id),
      staleTime: 60_000,
    })),
  });
}

// ===========================================================================
// Activity feed — cross-engagement audit fan-out. No unified endpoint
// exists today; T-0107 §6 flags this as a backend follow-up. v1 keeps
// the per-engagement fan-out + client-side merge.
// ===========================================================================

import { listEngagementAudit, type AuditEvent } from './audit';

export const ACTIVITY_FEED_LIMIT = 20;
const AUDIT_PER_ENGAGEMENT_LIMIT = 5;

export interface ActivityFeedItem {
  event: AuditEvent;
  engagement_id: string;
  engagement_name: string;
  client_name: string;
}

export function useActivityFeedQuery(engagements: ReadonlyArray<EngagementDto>): {
  items: ActivityFeedItem[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
} {
  const queries = useQueries({
    queries: engagements.map((eng) => ({
      queryKey: ['portfolio', 'engagement-audit', eng.id] as const,
      queryFn: () => listEngagementAudit(eng.id, { page_size: AUDIT_PER_ENGAGEMENT_LIMIT }),
      staleTime: 60_000,
    })),
  });

  const isLoading = queries.some((q) => q.isLoading);
  const errored = queries.find((q) => q.isError);
  const isError = Boolean(errored);

  const items: ActivityFeedItem[] = [];
  queries.forEach((q, idx) => {
    const eng = engagements[idx];
    if (!eng || !q.data) return;
    for (const event of q.data.events) {
      items.push({
        event,
        engagement_id: eng.id,
        engagement_name: eng.project_name,
        client_name: eng.client_name,
      });
    }
  });
  items.sort((a, b) => b.event.created_at.localeCompare(a.event.created_at));
  return {
    items: items.slice(0, ACTIVITY_FEED_LIMIT),
    isLoading,
    isError,
    error: errored?.error ?? null,
  };
}

// ===========================================================================
// Per-engagement detail fan-out — the portfolio table's row-level read.
// Each row needs phase_path, budget_rollup, open_issues_count from the T-0119
// hero DTO. The composite portfolio endpoint does NOT carry these (it carries
// posture + finding rollups only). For low-tens-of-engagements firms the
// fan-out is cheap; for larger firms we'd add the fields to the composite.
// React-Query de-duplicates against `useEngagementQuery(:id)` callers.
// ===========================================================================

export function useEngagementDetailsQuery(engagementIds: ReadonlyArray<string>) {
  return useQueries({
    queries: engagementIds.map((id) => ({
      queryKey: ['engagements', 'detail', id] as const,
      queryFn: () =>
        apiClient(`/api/v1/engagements/${encodeURIComponent(id)}`, EngagementDetailDtoSchema),
      staleTime: 30_000,
    })),
  });
}

export type { EngagementDetailDto };

// ===========================================================================
// Re-exports for downstream callers.
// ===========================================================================

export type { ReadinessTrendResult, OverallPosture, ReadinessScorecard, EngagementDto };
